import {
  type TranslateFunction,
  type TranslationAdapter,
  type TranslationHookResult,
} from './contracts';

/**
 * The injection seam: one module-scope adapter slot plus the no-adapter
 * fallback. Registered from app wiring at module scope (like
 * `registerAnalyticsAdapter`), so ordering holds by construction.
 *
 * Fallback = identity (the key comes back unchanged), in dev and prod alike —
 * that mirrors what `react-i18next` / `i18next.t` do today for a missing
 * instance or key (measured at `583a4516dc`). A stricter policy belongs to the
 * i18n team's implementation switch; `hasTranslationAdapter()` lets a caller
 * assert registration without this door deciding it.
 *
 * The same reasoning fixes the fallback locale at `undefined`: with nothing
 * registered there is no app to ask, and guessing `en` would hand a consumer a
 * locale-bearing URL or date format for a language nobody chose.
 */

let adapter: TranslationAdapter | null = null;

/** Register the implementation ONCE at module scope, before anything renders. */
export function registerTranslationAdapter(next: TranslationAdapter): void {
  adapter = next;
}

/** Whether an implementation has been registered. */
export function hasTranslationAdapter(): boolean {
  return adapter !== null;
}

/** Test-only: drop the registered adapter between tests. */
export function __resetTranslationAdapterForTests(): void {
  adapter = null;
}

/** The documented no-adapter fallback: return the key unchanged. */
export const identityTranslate: TranslateFunction = (key) => String(key);

/** The adapter's hook, or the identity fallback. */
export function resolveTranslationHook(): TranslationHookResult {
  if (adapter === null) return { t: identityTranslate, locale: undefined };
  return adapter.useTranslations();
}

/** The adapter's non-React accessor, or the identity fallback. */
export function resolveTranslate(): TranslateFunction {
  return adapter === null ? identityTranslate : adapter.t;
}
